import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TimeAgo from '../../../../components/utils/TimeAgo';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import {
  GetMfaBackupCodesBIZidentity
} from '../../../../redux/actions/business/identity/mfaBackupCodesActions';

const MfaBackupCodesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [codesList, setCodesList] = useState([]);
  const [codesTotal, setCodesTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // narrowing the result set while on a later page would show an empty table
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "User", sort: "user_id" },
    { label: "User type", sort: "user_type" },
    { label: "Status", sort: "used_at" },
    { label: "Used", sort: "used_at" },
    { label: "Time stamps", sort: "createdAt" },
  ];

  useEffect(() => {
    handleGetCodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection, statusFilter]);

  useEffect(() => {
    try {
      setCodesList(state.mfaBackupCodesList?.records || []);
      setCodesTotal(state.mfaBackupCodesList?.total || 0);
      setSummary(state.mfaBackupCodesList?.summary || null);
    } catch (err) {}
  }, [state]);

  async function handleGetCodes(customLoading){
    let query = "";
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    if(statusFilter) query += `&status=${statusFilter}`;
    await dispatch(GetMfaBackupCodesBIZidentity(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const summaryTiles = [
    { label: "Codes issued", value: Number(summary?.total || 0), accent: TILE_ACCENT.neutral },
    { label: "Unused", value: Number(summary?.remaining || 0), accent: TILE_ACCENT.good },
    { label: "Used", value: Number(summary?.used || 0), accent: TILE_ACCENT.warn },
  ];

  return (
    <Box>

      <TopBar
        title={"MFA Backup Codes"}
        search={search}
        searchPlaceholder={"Search id, user id or user type"}
        onSearchChange={handleSearchChange}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetCodes();
        }}
        />

      <SummaryTiles tiles={summaryTiles} />

      <Box className='flexCenter_Row' flexWrap={"wrap"} gap={"12px"} mb={"14px"}>
        <TextField
          select
          size='small'
          label='Status'
          sx={{ minWidth: "160px" }}
          value={statusFilter}
          onChange={handleStatusChange}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='unused'>Unused</MenuItem>
          <MenuItem value='used'>Used</MenuItem>
        </TextField>
      </Box>

      <Typography
        variant='caption'
        sx={{ color: "grey", display: "block", mb: "14px" }}>
        Issue and redemption records only. The code hash is never returned by
        the API, so no backup code can be recovered from this screen. Search by
        a user id to see how many of that user&apos;s codes are still unused.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          codesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No backup code found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                codesList.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      // a spent code is dimmed so remaining ones stand out
                      opacity: row.used_at ? 0.55 : 1,
                    }}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.user_id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {
                        row.user_type
                        ? <Chip 
                            size='small'
                            label={row.user_type} 
                            variant='outlined' 
                            color={row.user_type === "INTERNAL_USER" ? "primary" : "secondary"}
                            />
                        : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={row.used_at ? "Used" : "Unused"}
                        color={row.used_at ? "warning" : "success"}
                        variant={row.used_at ? "filled" : "outlined"}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.used_at
                        ? <TimeAgo value={row.used_at} />
                        : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={codesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default MfaBackupCodesPage;
